/** @jsxImportSource @emotion/react */
import { useTheme } from '@emotion/react';
import { useEffect, useState } from 'react';
import { useParams, Redirect } from 'react-router-dom';
import { FormProvider, useForm, UseFormSetValue } from 'react-hook-form';
import Navbar from '../Navbar';
import Step1 from 'pages/drugSubstance/Step1';
import Step2 from 'pages/drugSubstance/Step2';
import Step3 from 'pages/drugSubstance/Step3';
import FinalStep from './FinalStep';
import { API } from 'utils/drugSubstance';
import { useAdminGetSubstance, useAdminUpdateSubstance } from 'utils/hooks/admin.hook';
import CircularProgress from '@material-ui/core/CircularProgress';
import UnsavedChangeDialog from 'component/UnsavedChangeDialog';

function setFormData(data: any, setValue: UseFormSetValue<API>) {
    setValue('inn', data.inn || '');
    setValue('cas', data.cas || '');
    setValue('codeHs', data.codeHs || '');
    setValue('type', data.type || null);
    setValue('tree', data.tree || null);
}

function AdminDrugSubstance() {
    const theme = useTheme();
    const [redirectionPath, setRedirectionPath] = useState<string | null>(null);
    const [step, setStep] = useState(1);
    const [valuesChanged, setValuesChanged] = useState(false);
    const params = useParams<{ id: string }>();
    const { mutate: update } = useAdminUpdateSubstance(params.id);
    const { isLoading, isError, substance } = useAdminGetSubstance(params.id);
    const methods = useForm<API>({
        mode: 'onChange',
    });
    const { watch, setValue } = methods;

    const handleStep = (type: "decrement" | "increment") => {
        setStep(step => type === "decrement" ? step - 1 : step + 1);
    }

    useEffect(() => {
        if (substance) setFormData(substance, setValue);
      }, [substance, setValue]);

    const handleSave = () => { 
        update(watch());
    }

    if (isLoading) {
        return (
            <div css={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100vh',
            }}>
                <CircularProgress />
            </div>
        )
    }

    if (isError) {
        return <Redirect to="/admin" />
     }

    return (
        <>
            <UnsavedChangeDialog
                redirectionPath={redirectionPath}
                setRedirectionPath={setRedirectionPath}
                valuesChanged={valuesChanged}
                setValuesChanged={setValuesChanged}
                saveAction={handleSave}
                watch={watch}
            />
            <div
                css={{
                    flexGrow: 1,
                    backgroundColor: theme.white,
                    color: theme.black,
                    display: 'flex',
                }}
            >
                <Navbar customRedirection={setRedirectionPath} />
                <FormProvider {...methods}>
                    <form
                        css={{
                            flexGrow: 1,
                            overflowY: 'auto'
                        }}
                    >
                    {step <= 1 && <Step1 handleSaveBeforeLeave={setRedirectionPath} handleStep={handleStep} />}
                    {step === 2 && <Step2 handleStep={handleStep} />}
                    {step === 3 && <Step3 setValuesChanged={setValuesChanged} plants={[]} handleStep={handleStep} />}
                    {step === 4 && <FinalStep handleStep={handleStep} />}
                    </form>
                </FormProvider>
            </div>
        </>
    );
}

export default AdminDrugSubstance;